import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Heading,
  HStack,
  VStack,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Code,
  useToast,
  Spinner,
  Divider,
  Link,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import MergeSubmissionsModal from './MergeSubmissionsModal';

/**
 * Admin panel for the LDX File API integration.
 *
 * - "Run sync now" calls /api/ldx/sync with the admin bearer token. The same
 *   endpoint runs every 5 minutes via Vercel Cron.
 * - Inbound table shows the most recent pulled HL7 orders awaiting mapping.
 * - Outbound table shows queued status uploads (pending + recently sent/failed).
 *
 * Mount this somewhere in AdminDashboard — e.g. as a new tab "Lab API".
 */
export default function LdxControls() {
  const [inbound, setInbound] = useState([]);
  const [queue, setQueue] = useState([]);
  const [patientNames, setPatientNames] = useState({}); // submission_id -> patient_name
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lastResult, setLastResult] = useState(null);
  const [mergePair, setMergePair] = useState(null); // { primary, duplicate } for the merge modal
  const toast = useToast();

  // Merge a mapped submission with another (e.g. its original) from this page.
  const startMerge = async (submissionId) => {
    const other = window.prompt(`Merge submission #${submissionId} with which other submission ID?\n(The duplicate is cancelled and merged into the one you keep.)`);
    if (!other) return;
    const otherId = parseInt(String(other).replace(/[^0-9]/g, ''), 10);
    if (!otherId || otherId === submissionId) return;
    const { data, error } = await supabase.from('submissions').select('*').in('id', [submissionId, otherId]);
    if (error || !data || data.length < 2) {
      toast({ title: 'Could not load both submissions', description: error?.message, status: 'error', duration: 4000 });
      return;
    }
    setMergePair({
      primary: data.find((d) => d.id === submissionId),
      duplicate: data.find((d) => d.id === otherId),
    });
  };

  const loadTables = useCallback(async () => {
    setLoading(true);
    const [inboundRes, queueRes] = await Promise.all([
      supabase
        .from('ldx_inbound_orders')
        .select('id, transaction_id, source_filename, received_at, submission_id, processed_at, echo, echo_of_submission_id, echo_detected_by')
        .order('received_at', { ascending: false })
        .limit(20),
      supabase
        .from('ldx_outbound_queue')
        .select('id, submission_id, ldx_message_id, status, attempts, created_at, sent_at, last_error')
        .order('created_at', { ascending: false })
        .limit(20),
    ]);
    const inboundData = inboundRes.error ? [] : (inboundRes.data || []);
    const queueData = queueRes.error ? [] : (queueRes.data || []);
    if (!inboundRes.error) setInbound(inboundData);
    if (!queueRes.error) setQueue(queueData);

    // Resolve patient names for every referenced submission (mapped, echo, queued).
    const ids = [...new Set([
      ...inboundData.map((r) => r.submission_id),
      ...inboundData.map((r) => r.echo_of_submission_id),
      ...queueData.map((r) => r.submission_id),
    ].filter(Boolean))];
    if (ids.length) {
      const { data: subs } = await supabase.from('submissions').select('id, patient_name').in('id', ids);
      const map = {};
      (subs || []).forEach((s) => { map[s.id] = s.patient_name; });
      setPatientNames(map);
    } else {
      setPatientNames({});
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadTables();
  }, [loadTables]);

  const runSync = async () => {
    setRunning(true);
    setLastResult(null);
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      if (!adminUser?.id) {
        toast({ title: 'Not signed in', description: 'Log out and back in, then retry.', status: 'error' });
        setRunning(false);
        return;
      }
      const res = await fetch('/api/ldx-sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_id: adminUser.id }),
      });
      const body = await res.json();
      setLastResult({ status: res.status, body });
      if (res.ok) {
        toast({
          title: 'Sync complete',
          description: `Received ${body.pickup?.received ?? 0}, sent ${body.flush?.sent ?? 0}, failed ${body.flush?.failed ?? 0}`,
          status: body.pickup?.errors?.length || body.flush?.errors?.length ? 'warning' : 'success',
        });
      } else {
        toast({ title: 'Sync failed', description: body.error || `HTTP ${res.status}`, status: 'error' });
      }
      await loadTables();
    } catch (err) {
      toast({ title: 'Sync request error', description: String(err), status: 'error' });
    } finally {
      setRunning(false);
    }
  };

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Heading size="md">LDX Lab API</Heading>
        <HStack>
          <Button onClick={loadTables} isDisabled={loading} variant="outline">
            Refresh
          </Button>
          <Button colorScheme="blue" onClick={runSync} isLoading={running}>
            Run sync now
          </Button>
        </HStack>
      </HStack>

      <Text color="gray.600" mb={4} fontSize="sm">
        Cron runs every 5 minutes. Each run pulls pending HL7 orders from the lab and
        flushes any queued status uploads. Use "Run sync now" to force a manual run.
      </Text>

      {lastResult && (
        <Box mb={4} p={3} bg="gray.50" borderRadius="md" fontSize="sm">
          <Text fontWeight="bold" mb={1}>Last run (HTTP {lastResult.status})</Text>
          <Code display="block" whiteSpace="pre" p={2} fontSize="xs">
            {JSON.stringify(lastResult.body, null, 2)}
          </Code>
        </Box>
      )}

      <VStack align="stretch" spacing={6}>
        <Box>
          <Heading size="sm" mb={2}>
            Inbound orders (latest 20)
          </Heading>
          {loading ? (
            <Spinner />
          ) : inbound.length === 0 ? (
            <Text color="gray.500" fontSize="sm">No inbound messages yet.</Text>
          ) : (
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Received</Th>
                  <Th>Patient</Th>
                  <Th>Transaction ID</Th>
                  <Th>Source file</Th>
                  <Th>Mapped to submission?</Th>
                </Tr>
              </Thead>
              <Tbody>
                {inbound.map((row) => (
                  <Tr key={row.id}>
                    <Td whiteSpace="nowrap">{new Date(row.received_at).toLocaleString()}</Td>
                    <Td fontSize="sm">{patientNames[row.submission_id || row.echo_of_submission_id] || '—'}</Td>
                    <Td><Code fontSize="xs">{row.transaction_id}</Code></Td>
                    <Td fontSize="xs">{row.source_filename || '—'}</Td>
                    <Td>
                      {row.echo ? (
                        <Link
                          as={RouterLink}
                          to={row.echo_of_submission_id ? `/admin/submissions/${row.echo_of_submission_id}` : '#'}
                          title={`Echo of our own export (matched by ${row.echo_detected_by || 'rule'})`}
                        >
                          <Badge colorScheme="red" cursor="pointer" _hover={{ bg: 'red.200' }}>
                            echo of #{row.echo_of_submission_id} — quarantined
                          </Badge>
                        </Link>
                      ) : row.submission_id ? (
                        <HStack spacing={2}>
                          <Link
                            as={RouterLink}
                            to={`/admin/submissions/${row.submission_id}`}
                            title={`View submission #${row.submission_id}`}
                          >
                            <Badge colorScheme="green" cursor="pointer" _hover={{ bg: 'green.200' }}>
                              mapped → #{row.submission_id}
                            </Badge>
                          </Link>
                          <Button size="xs" variant="outline" colorScheme="purple" onClick={() => startMerge(row.submission_id)}>
                            Merge
                          </Button>
                        </HStack>
                      ) : (
                        <Badge colorScheme="orange">awaiting mapping</Badge>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Box>

        <Divider />

        <Box>
          <Heading size="sm" mb={2}>
            Outbound queue (latest 20)
          </Heading>
          {loading ? (
            <Spinner />
          ) : queue.length === 0 ? (
            <Text color="gray.500" fontSize="sm">No queued uploads.</Text>
          ) : (
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Created</Th>
                  <Th>Patient</Th>
                  <Th>LDX message</Th>
                  <Th>Status</Th>
                  <Th>Attempts</Th>
                  <Th>State</Th>
                  <Th>Last error</Th>
                </Tr>
              </Thead>
              <Tbody>
                {queue.map((row) => (
                  <Tr key={row.id}>
                    <Td whiteSpace="nowrap">{new Date(row.created_at).toLocaleString()}</Td>
                    <Td fontSize="sm">{patientNames[row.submission_id] || '—'}</Td>
                    <Td><Code fontSize="xs">{row.ldx_message_id}</Code></Td>
                    <Td>{row.status}</Td>
                    <Td>{row.attempts}</Td>
                    <Td>
                      {row.sent_at ? (
                        <Badge colorScheme="green">sent</Badge>
                      ) : row.last_error ? (
                        <Badge colorScheme="red">failed</Badge>
                      ) : (
                        <Badge colorScheme="yellow">pending</Badge>
                      )}
                    </Td>
                    <Td fontSize="xs" color="red.600" maxW="300px" isTruncated title={row.last_error || ''}>
                      {row.last_error || ''}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Box>
      </VStack>

      {mergePair && (
        <MergeSubmissionsModal
          isOpen={!!mergePair}
          onClose={() => setMergePair(null)}
          primary={mergePair.primary}
          duplicate={mergePair.duplicate}
          onMerged={() => { setMergePair(null); loadTables(); }}
        />
      )}
    </Box>
  );
}
